import { useMarkupContext } from "@context-providers/markup/markup-context";
import { useMembers } from "@hooks/use-members";
import { Box, Stack } from "@mui-wrapper";
import { ProjectMarkupsBoardCard } from "@pages/project-details/project-markups/board/project-markups-board-card";
import { ProjectMarkupsBoardColumn } from "@pages/project-details/project-markups/board/project-markups-board-column";
import { getMarkupDueDateUrgency, getMarkupsBoardColumns, MARKUPS_BOARD_STATUSES } from "@pages/project-details/project-markups/board/project-markups-board-utils";
import { getMarkupAssigneeMember } from "@pages/project-details/project-markups/markup-assignee-utils";
import { getMarkupsDueDateFilterOptions } from "@pages/project-details/project-markups/project-markups-filters/project-markups-filter-list";
import { getMarkupStatusValue } from "@pages/project-details/project-markups/status/markup-status-utils";
import { useChangeMarkupStatus } from "@pages/project-details/project-markups/status/use-change-markup-status";
import { AnnotationsBoardEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Status each markup is being moved to, by markup ID */

/** Returns the pending statuses without the one of the markup */
function withoutPendingStatus(pendingStatuses, markupId) {
  return Object.fromEntries(Object.entries(pendingStatuses).filter(([id]) => id !== markupId));
}
/** Columns of the board, one per status, where the markups can be dragged to change their status */
export function ProjectMarkupsBoardColumns({
  markups,
  onMarkupClick
}) {
  const {
    hasPermissionToEditMarkup,
    selectedMarkup,
    selectedMarkupId
  } = useMarkupContext();
  const {
    companyMembers,
    projectMembers
  } = useMembers();
  const changeMarkupStatus = useChangeMarkupStatus();
  const {
    trackEvent
  } = useTrackEvent();
  const [draggedMarkup, setDraggedMarkup] = useState();
  const [pendingStatuses, setPendingStatuses] = useState({});

  // The moved markups are shown in their new column while their status is saved
  const columns = getMarkupsBoardColumns(markups, pendingStatuses);
  const dueDateFilters = getMarkupsDueDateFilterOptions();
  const draggedStatus = draggedMarkup && getMarkupStatusValue(draggedMarkup.status?.value);
  async function moveMarkup(status) {
    const markup = draggedMarkup;
    setDraggedMarkup(undefined);
    const fromStatus = getMarkupStatusValue(markup?.status?.value);
    if (!markup || fromStatus === status) {
      return;
    }
    setPendingStatuses(previous => ({
      ...previous,
      [markup.id]: status
    }));
    trackEvent({
      name: AnnotationsBoardEvents.moveAnnotation,
      props: {
        fromStatus,
        toStatus: status
      }
    });
    try {
      await changeMarkupStatus(markup, status);
    } finally {
      // On failure the markup goes back to the column of its stored status
      setPendingStatuses(previous => withoutPendingStatus(previous, markup.id));
    }
  }
  return (
    // A single scroll for the whole board, in both directions, so the columns scroll together
    <Box sx={{
      flexGrow: 1,
      minWidth: 0,
      overflow: "auto"
    }}>
      {/* At least as tall as the board, so short columns still reach its bottom */}
      <Stack direction="row" sx={{
        gap: 1,
        minHeight: "100%"
      }}>
        {MARKUPS_BOARD_STATUSES.map(status_0 => <ProjectMarkupsBoardColumn key={status_0} status={status_0} count={columns[status_0].length} canDrop={!!draggedMarkup && status_0 !== draggedStatus} onDrop={newStatus => void moveMarkup(newStatus)}>
            {columns[status_0].map(markup_0 => <ProjectMarkupsBoardCard key={markup_0.id} markup={markup_0} assignee={getMarkupAssigneeMember({
            assignee: markup_0.assignee,
            companyMembers,
            projectMembers
          })} dueDateUrgency={getMarkupDueDateUrgency(markup_0, dueDateFilters)} isSelected={markup_0.id === selectedMarkupId || markup_0.id === selectedMarkup?.id} isDraggable={hasPermissionToEditMarkup && !pendingStatuses[markup_0.id]} isPending={!!pendingStatuses[markup_0.id]} onClick={onMarkupClick} onDragStart={setDraggedMarkup} onDragEnd={() => setDraggedMarkup(undefined)} />)}
          </ProjectMarkupsBoardColumn>)}
      </Stack>
    </Box>
  );
}